import {useEffect, useState} from "react";
import {getWsStatus, subscribeWsStatus} from "../../../../util/wsStatus";

// A drop shorter than this stays invisible (the socket retries every 2s).
const QUIET_MS = 3000;
// Down longer than this, or the device is offline: say so and offer Retry.
const OFFLINE_MS = 30000;

const toLiveStatus = ({status, downSince}, online, now) => {
  if (!online || status === 'closed') return 'offline';
  if (status === 'open') return 'live';
  if (downSince === null) return 'connecting';
  const downFor = now - downSince;
  if (downFor < QUIET_MS) return 'live';
  return downFor < OFFLINE_MS ? 'reconnecting' : 'offline';
};

// What the chat shows about the inbox socket: 'live' | 'connecting' | 'reconnecting' | 'offline'.
export const useLiveStatus = () => {
  const [ws, setWs] = useState(getWsStatus);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [now, setNow] = useState(Date.now);

  useEffect(() => subscribeWsStatus(() => {
    setWs(getWsStatus());
    setNow(Date.now());
  }), []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  // Re-render when the drop crosses the next threshold.
  useEffect(() => {
    if (ws.downSince === null) return undefined;
    const downFor = Date.now() - ws.downSince;
    const next = [QUIET_MS, OFFLINE_MS].find(threshold => threshold > downFor);
    if (next === undefined) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), next - downFor);
    return () => clearTimeout(timer);
  }, [ws, now]);

  return toLiveStatus(ws, online, now);
};
